/**
 * First-Value Guided Onboarding Overlay
 *
 * Hybrid overlay: light translucent mask (focus + lock, no blur) + repositioned
 * card (visibility) + logo lift strip so the brand stays readable.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, CheckCircle, ArrowRight, Sparkles, UserPlus, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useFirstValueOnboarding } from '../contexts/FirstValueOnboardingContext';
import { toast } from 'sonner';

/**
 * Translucent mask only.
 *
 * z-index strategy:
 *   sidebar / mobile header: z-40 / z-50
 *   MASK:                    z-[60]  -> dims background, blocks clicks (no blur)
 *   CARD:                    z-[70]  -> only CTAs interactive
 *   FVO STARTER EVENT (cal): z-[65]  -> raised above mask via .fvo-event-above-mask
 *
 * Note: there is intentionally NO logo backdrop / no header strip — the global
 * mask handles dimming and the brand reads through it at bg-black/20.
 */
function GuidedMask() {
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/20 pointer-events-auto cursor-not-allowed animate-in fade-in duration-200"
      data-testid="fvo-mask"
      aria-hidden="true"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
    />
  );
}

/**
 * Overlay shown on the Job Detail page during first-value onboarding
 * Step: "job_detail"
 */
export function GuidedJobDetailOverlay() {
  const navigate = useNavigate();
  const { scheduleVisit, step, loading: contextLoading, active } = useFirstValueOnboarding();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (contextLoading) return null;
  if (!active || step !== 'job_detail') return null;

  const handleSchedule = async () => {
    setLoading(true);
    const result = await scheduleVisit();
    setLoading(false);

    if (result.success) {
      toast.success('Visit scheduled!');
      navigate('/calendar');
    } else {
      toast.error('Failed to schedule visit');
    }
  };

  return (
    <>
      <GuidedMask />

      {/* Desktop: Top-right floating card */}
      <div className="hidden md:block fixed top-24 right-4 z-[70] w-80 pointer-events-auto animate-in slide-in-from-right-5 duration-300">
        <div className="bg-white rounded-xl shadow-2xl border border-orange-200 overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-orange-500 to-orange-400" />
          <div className="p-4">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold text-zinc-900 text-sm">Sample job created</h3>
                <p className="text-xs text-zinc-500 mt-0.5">See how scheduling works</p>
              </div>
            </div>

            <p className="text-sm text-zinc-600 mb-4">
              We created a sample job so you can see how scheduling works. Try scheduling it now!
            </p>

            <Button
              onClick={handleSchedule}
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white h-10 text-sm font-medium"
              data-testid="schedule-first-job-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Scheduling...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Schedule This Job
                  <ArrowRight className="w-3 h-3" />
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile: Bottom sheet */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-[70] pointer-events-auto animate-in slide-in-from-bottom-5 duration-300">
        <div className="bg-white rounded-t-2xl shadow-2xl border-t border-orange-200 px-5 pt-3 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
          <div className="flex justify-center pb-3">
            <div className="w-10 h-1.5 bg-zinc-300 rounded-full" />
          </div>

          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-orange-600" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-zinc-900 text-base leading-tight">Sample job ready</h3>
              <p className="text-sm text-zinc-500 leading-tight">Schedule it to see how visits appear on your calendar</p>
            </div>
          </div>

          <Button
            onClick={handleSchedule}
            disabled={loading}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white h-12 text-base font-semibold"
            data-testid="schedule-first-job-btn-mobile"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Scheduling...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Schedule This Job
              </span>
            )}
          </Button>
        </div>
      </div>
    </>
  );
}

/**
 * Reusable secondary button class — keeps strong contrast across hover/focus/active.
 * Fixes the prior bug where outline variant rendered "invisible" text on hover.
 */
const SECONDARY_BTN_CLASSES =
  'w-full font-semibold border-2 border-zinc-300 bg-white text-zinc-800 ' +
  'hover:bg-zinc-100 hover:text-zinc-900 hover:border-zinc-400 ' +
  'focus:bg-zinc-100 focus:text-zinc-900 focus-visible:ring-2 focus-visible:ring-zinc-400 ' +
  'active:bg-zinc-200 active:text-zinc-900 ' +
  'disabled:bg-zinc-100 disabled:text-zinc-500';

/**
 * Overlay shown on the Calendar page after scheduling
 * Step: "calendar_scheduled"
 */
export function GuidedCalendarSuccessOverlay() {
  const navigate = useNavigate();
  const { convertToReal, startFresh, step, loading: contextLoading, active, starterVisitId } = useFirstValueOnboarding();
  const [loading, setLoading] = useState(null);
  const [showTransition, setShowTransition] = useState(false);

  // Auto-scroll to show the scheduled visit AND raise it above the mask so it
  // becomes the visually dominant element (the "aha" moment).
  useEffect(() => {
    if (!active || step !== 'calendar_scheduled') return;

    const ELEVATE_CLASS = 'fvo-event-above-mask';
    const elevatedEls = new Set();

    const findVisitElements = () => {
      const ids = [starterVisitId].filter(Boolean);
      const selectors = [];
      ids.forEach((id) => {
        selectors.push(`[data-testid="resizable-event-${id}"]`);
        selectors.push(`[data-testid="event-${id}"]`);
        selectors.push(`[data-testid="week-event-${id}"]`);
        selectors.push(`[data-testid="day-event-${id}"]`);
      });
      const out = [];
      for (const sel of selectors) {
        document.querySelectorAll(sel).forEach((el) => out.push(el));
      }
      return out;
    };

    const elevateAndScroll = () => {
      const els = findVisitElements();
      if (els.length === 0) return false;
      els.forEach((el) => {
        if (!elevatedEls.has(el)) {
          el.classList.add(ELEVATE_CLASS);
          elevatedEls.add(el);
        }
      });
      try { els[0].scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' }); } catch (_) {}
      return true;
    };

    // Try a few times to catch async event render
    let attempts = 0;
    const interval = setInterval(() => {
      const found = elevateAndScroll();
      attempts += 1;
      if (found || attempts > 8) {
        clearInterval(interval);
      }
    }, 350);

    // Fallback grid scroll if no event yet
    const fallbackTimer = setTimeout(() => {
      if (elevatedEls.size === 0) {
        const grid =
          document.querySelector('[data-testid="calendar-grid"]') ||
          document.querySelector('.fc-timegrid-body') ||
          document.scrollingElement;
        if (grid && 'scrollTop' in grid) {
          try { grid.scrollTo({ top: 320, behavior: 'smooth' }); } catch (_) { grid.scrollTop = 320; }
        }
      }
    }, 1500);

    return () => {
      clearInterval(interval);
      clearTimeout(fallbackTimer);
      // Clean up the elevation class so subsequent app states don't keep the
      // event lifted above the mask.
      elevatedEls.forEach((el) => el.classList.remove(ELEVATE_CLASS));
    };
  }, [active, step, starterVisitId]);

  if (contextLoading) return null;
  if (!active || step !== 'calendar_scheduled') return null;

  const handleUseThisJob = async () => {
    setLoading('convert');
    const result = await convertToReal();
    setLoading(null);

    if (result.success) {
      toast.success('Great! Your job is ready. Edit the details to make it yours.');
      navigate(`/customers/${result.customerId}`);
    } else {
      toast.error('Something went wrong');
    }
  };

  const handleStartFresh = async () => {
    setShowTransition(true);
    await new Promise((resolve) => setTimeout(resolve, 1200));

    setLoading('fresh');
    const result = await startFresh();
    setLoading(null);

    if (result.success) {
      // Land on /customers with onboarding flag — Customers.js renders the
      // RealFirstJobGuidedFlow (intro + multi-step modal).
      navigate('/customers?onboarding=real_first_job');
    } else {
      setShowTransition(false);
      toast.error('Something went wrong');
    }
  };

  // Transition state
  if (showTransition) {
    return (
      <>
        <GuidedMask />
        {/* Desktop */}
        <div className="hidden md:block fixed top-24 right-4 z-[70] w-96 pointer-events-auto">
          <div className="bg-white rounded-xl shadow-2xl border border-green-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-zinc-900">Great — now let&apos;s create your real first job.</h3>
              </div>
            </div>
            <p className="text-sm text-zinc-600">
              We&apos;ll add the customer and job details together, then schedule it.
            </p>
            <div className="mt-3 flex items-center gap-2 text-sm text-green-600">
              <span className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
              Getting ready...
            </div>
          </div>
        </div>

        {/* Mobile */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-[70] pointer-events-auto">
          <div className="bg-white rounded-t-2xl shadow-2xl border-t border-green-200 px-5 pt-3 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
            <div className="flex justify-center pb-3">
              <div className="w-10 h-1.5 bg-zinc-300 rounded-full" />
            </div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <UserPlus className="w-5 h-5 text-green-600" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-zinc-900 text-base leading-tight">
                  Great — now let&apos;s create your real first job.
                </h3>
                <p className="text-sm text-zinc-500 leading-tight">We&apos;ll add the customer and job details together, then schedule it.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-green-600">
              <span className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
              Getting ready...
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <GuidedMask />

      {/* Desktop: Top-right card */}
      <div className="hidden md:block fixed top-24 right-4 z-[70] w-80 pointer-events-auto animate-in slide-in-from-right-5 duration-300">
        <div className="bg-white rounded-xl shadow-2xl border border-green-200 overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-green-500 to-green-400" />
          <div className="p-4">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-zinc-900 text-sm">Visit scheduled!</h3>
                <p className="text-xs text-zinc-500 mt-0.5">This is your job on the calendar.</p>
              </div>
            </div>

            <p className="text-sm text-zinc-600 mb-4">
              This is how your schedule works. What would you like to do?
            </p>

            <div className="space-y-2">
              <Button
                onClick={handleStartFresh}
                disabled={loading !== null}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white h-10 text-sm font-semibold"
                data-testid="start-fresh-btn"
              >
                {loading === 'fresh' ? (
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Clearing...
                  </span>
                ) : (
                  'Create My Real First Job'
                )}
              </Button>

              <Button
                onClick={handleUseThisJob}
                disabled={loading !== null}
                variant="outline"
                className={`${SECONDARY_BTN_CLASSES} h-10 text-sm`}
                data-testid="use-this-job-btn"
              >
                {loading === 'convert' ? (
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 border-2 border-zinc-500 border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : (
                  'Keep This Job'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: Bottom sheet */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-[70] pointer-events-auto animate-in slide-in-from-bottom-5 duration-300">
        <div className="bg-white rounded-t-2xl shadow-2xl border-t border-green-200 px-5 pt-3 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
          <div className="flex justify-center pb-3">
            <div className="w-10 h-1.5 bg-zinc-300 rounded-full" />
          </div>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-zinc-900 text-base leading-tight">Visit scheduled!</h3>
              <p className="text-sm text-zinc-500 leading-tight">This is your job on the calendar.</p>
            </div>
          </div>

          <div className="space-y-2.5">
            <Button
              onClick={handleStartFresh}
              disabled={loading !== null}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white h-12 text-base font-semibold"
              data-testid="start-fresh-btn-mobile"
            >
              {loading === 'fresh' ? 'Clearing...' : 'Create My Real First Job'}
            </Button>

            <Button
              onClick={handleUseThisJob}
              disabled={loading !== null}
              variant="outline"
              className={`${SECONDARY_BTN_CLASSES} h-12 text-base`}
              data-testid="use-this-job-btn-mobile"
            >
              {loading === 'convert' ? 'Saving...' : 'Keep This Job'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Inline guidance banner (legacy "customer" / "job" types still used by other
 * pages). The real-first-job continuation flow on /customers uses
 * RealFirstJobGuidedFlow instead.
 */
export function OnboardingGuidanceBanner({ type = 'customer' }) {
  const messages = {
    customer: {
      title: 'Add your first customer',
      description: "Start by adding your customer's info. You'll use this for jobs, estimates, and invoices."
    },
    job: {
      title: 'Create your first job',
      description: "Now create a job for your customer. You can add details, schedule visits, and track progress."
    }
  };

  const msg = messages[type] || messages.customer;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Info className="w-4 h-4 text-blue-600" />
        </div>
        <div>
          <h4 className="font-medium text-blue-900 text-sm">{msg.title}</h4>
          <p className="text-sm text-blue-700 mt-0.5">{msg.description}</p>
        </div>
      </div>
    </div>
  );
}
